import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Star, Store, MoreVertical, EyeOff, Eye, Shield, Gift } from "lucide-react";
import GiftButton from "./GiftButton";
import { fileUrl } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import UserGem from "./UserGem";
import SmartImage from "./SmartImage";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export default function CommunityPostCard({ post, onChange, onOpen, onDelete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(!!post.liked);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [busy, setBusy] = useState(false);

  const isMine = user?.user_id === post.author_id;
  const isAdmin = user?.role === "admin" || user?.role === "master_admin";
  const isMaster = user?.role === "master_admin";

  const onLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/posts/${post.post_id}/like`);
      setLiked(data.liked);
      setLikeCount((c) => Math.max(0, c + (data.liked ? 1 : -1)));
      onChange?.({ ...post, liked: data.liked, like_count: Math.max(0, likeCount + (data.liked ? 1 : -1)) });
    } catch {
      toast.error("Couldn't like");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this post?")) return;
    try {
      // admins use the moderation endpoint (master only); authors use the normal endpoint
      const url = isAdmin && !isMine ? `/admin/posts/${post.post_id}` : `/community/posts/${post.post_id}`;
      await api.delete(url);
      toast.success("Post deleted");
      onDelete?.(post.post_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const onToggleHide = async (e) => {
    e?.stopPropagation();
    const next = !post.is_hidden;
    const reason = next ? window.prompt("Reason for hiding this post (optional)") || "" : "";
    try {
      await api.post(`/admin/posts/${post.post_id}/suspend`, null, { params: { suspended: next, reason } });
      toast.success(next ? "Post hidden" : "Post restored");
      onChange?.({ ...post, is_hidden: next });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't moderate");
    }
  };

  const firstMedia = post.media_paths?.[0];
  const firstType = post.media_types?.[0] || "image";

  return (
    <article
      className={`rounded-3xl bg-zinc-950 border overflow-hidden ${post.is_hidden ? "border-amber-400/40" : "border-white/10"}`}
      data-testid={`community-post-${post.post_id}`}
    >
      {post.is_hidden && (
        <div className="px-3 py-1.5 bg-amber-400/15 text-amber-200 text-[11px] font-bold uppercase tracking-widest border-b border-amber-400/30 inline-flex items-center gap-1" data-testid={`post-hidden-banner-${post.post_id}`}>
          <Shield className="w-3 h-3" /> Hidden by moderator
        </div>
      )}
      <header className="p-3 flex items-center gap-3">
        <button
          onClick={() => navigate(`/u/${post.author_id}`)}
          className="w-10 h-10 rounded-full bg-pink-500/20 grid place-items-center overflow-hidden border border-pink-400/30 shrink-0"
          data-testid={`post-author-${post.post_id}`}
        >
          {post.author_avatar ? (
            <img src={fileUrl(post.author_avatar)} alt={post.author_name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-pink-200 font-bold">{(post.author_name || "?")[0]}</span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-bold leading-tight truncate inline-flex items-center gap-1">
            {post.author_name}
            <UserGem userId={post.author_id} size="xs" />
          </p>
          <p className="text-[11px] text-zinc-500">{timeAgo(post.created_at)}</p>
        </div>
        {post.rating != null && (
          <div className="flex items-center gap-0.5 text-amber-300" data-testid={`post-rating-${post.post_id}`}>
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} className={`w-3.5 h-3.5 ${s <= post.rating ? "fill-amber-400" : "text-zinc-700"}`} />
            ))}
          </div>
        )}
        {(isMine || isAdmin) && (
          <div className="flex items-center gap-1">
            {isAdmin && !isMine && (
              <button
                onClick={onToggleHide}
                className="h-8 w-8 grid place-items-center rounded-full text-amber-300 hover:bg-amber-400/10"
                data-testid={`post-moderate-${post.post_id}`}
                title={post.is_hidden ? "Restore post" : "Hide post (moderation)"}
              >
                {post.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            )}
            {(isMine || isMaster) && (
              <button
                onClick={onRemove}
                className="h-8 w-8 grid place-items-center rounded-full text-zinc-400 hover:text-red-400 hover:bg-white/5"
                data-testid={`post-delete-${post.post_id}`}
                aria-label="Delete post"
                title={isMine ? "Delete" : "Permanent delete (Master Admin)"}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </header>

      {firstMedia && (
        <button
          onClick={() => onOpen?.(post)}
          className="block w-full aspect-square bg-black relative overflow-hidden"
          data-testid={`post-media-${post.post_id}`}
        >
          {firstType === "video" ? (
            <video
              src={fileUrl(firstMedia)}
              className="w-full h-full object-cover"
              playsInline
              muted
              loop
              autoPlay
            />
          ) : (
            <SmartImage
              src={fileUrl(firstMedia)}
              alt=""
              mode="smart_auto_fit"
              className="w-full h-full"
              testid={`post-media-image-${post.post_id}`}
            />
          )}
        </button>
      )}

      <div className="p-3 space-y-2">
        {post.caption && <p className="text-sm whitespace-pre-wrap">{post.caption}</p>}

        {post.product && (
          <button
            onClick={() => navigate(`/vendor/${post.product.vendor_id || ""}`)}
            className="w-full text-left flex items-center gap-2 p-2 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07]"
            data-testid={`post-product-${post.post_id}`}
          >
            {post.product.image_paths?.[0] && (
              <img
                src={fileUrl(post.product.image_paths[0])}
                alt={post.product.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Purchased</p>
              <p className="text-sm font-bold truncate">{post.product.name}</p>
            </div>
          </button>
        )}

        {post.tagged_vendor_ids?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`post-tags-${post.post_id}`}>
            {post.tagged_vendor_ids.map((vid) => (
              <button
                key={vid}
                onClick={() => navigate(`/vendor/${vid}`)}
                className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30 hover:bg-cyan-400/25"
                data-testid={`post-tag-${post.post_id}-${vid}`}
              >
                <Store className="w-3 h-3" /> @vendor
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4 pt-1">
          <button
            onClick={onLike}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-pink-300"
            data-testid={`post-like-${post.post_id}`}
          >
            <Heart className={`w-5 h-5 ${liked ? "fill-pink-500 text-pink-500" : ""}`} />
            <span className="font-bold">{likeCount}</span>
          </button>
          <button
            onClick={() => onOpen?.(post)}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"
            data-testid={`post-comments-${post.post_id}`}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-bold">{post.comment_count || 0}</span>
          </button>
          <GiftButton
            recipient={{ user_id: post.author_id, name: post.author_name, picture: post.author_avatar }}
            targetKind="community_post"
            targetId={post.post_id}
            variant="compact"
            count={post.gift_count || 0}
            testIdPrefix={`post-gift-${post.post_id}`}
          />
        </div>
      </div>
    </article>
  );
}
